/**
Encode PCM channel data as a RIFF/WAVE file.

Derived from https://stackoverflow.com/a/42632646/68210, which used to live in
www/lib/wave-worker.js as hand-committed JavaScript that nothing built, typed or
tested. The encoding is pure — no worker, no DOM — so it can be tested directly;
wave-worker.civet is the thin entry point that runs it off the main thread.

Status: Acceptable
*/

// Depths the writer below can emit. 8-bit is unsigned with a midpoint of 128,
// as the RIFF PCM format requires; every wider depth is signed two's
// complement, little endian.
export type BitDepth = 8 | 16 | 24 | 32

export type WavePCMConfig = {
  sampleRate?: number
  bitDepth?: BitDepth
}

// RIFF header through the end of the data chunk length, for canonical PCM: a
// 12-byte RIFF chunk, a 24-byte fmt chunk and an 8-byte data chunk header.
HEADER_LENGTH := 44

SUPPORTED_BIT_DEPTHS := [8, 16, 24, 32]

{ trunc } := Math

clamp := (sample: number): number ->
  if sample > 1
    1
  else if sample < -1
    -1
  else
    sample

// Two's complement has one more code below zero than above it, so full scale is
// asymmetric: a positive sample scales by 2^(n-1) - 1 and a negative one by
// 2^(n-1). Scaling both by 2^(n-1) would send +1.0 one code past the maximum,
// where it wraps to the most negative value — an audible click at full scale
// rather than the intended peak.
//
// Truncation rather than rounding, which is what the original did. It biases the
// error toward zero by up to half an LSB; at the 24-bit default that floor sits
// near -144 dBFS, so it is not worth changing the rendered output over.
quantize := (sample: number, bitDepth: number): number ->
  clamped := clamp sample
  fullScale := 2 ** (bitDepth - 1)

  if clamped > 0
    trunc clamped * (fullScale - 1)
  else
    trunc clamped * fullScale

// 8-bit PCM is unsigned, centred on 128. Clamped at the top because +1.0 scales
// to 256, which wraps to 0 — silence where the loudest sample should be.
quantizeUnsigned8 := (sample: number): number ->
  value := trunc (clamp(sample) + 1) * 128

  if value > 255 then 255 else value

/**
Encode one WAV file from per-channel PCM samples in [-1, 1].

`pcmArrays` is one Float32Array per channel, all the same length; they are
interleaved into frames in channel order.
*/
export encodeWavePCM := (pcmArrays: Float32Array[], config: WavePCMConfig = {}): Uint8Array ->
  sampleRate := config.sampleRate ?? 48000
  bitDepth := config.bitDepth ?? 16

  unless SUPPORTED_BIT_DEPTHS.includes bitDepth
    throw new Error `Unsupported bit depth ${bitDepth}: only 8, 16, 24 and 32 bits per sample are supported`

  numberOfChannels := pcmArrays.length
  unless numberOfChannels > 0
    throw new Error "Must pass at least one channel of PCM data to encode a .wav"

  frameCount := pcmArrays[0].length

  c .= 0
  while c < numberOfChannels
    unless pcmArrays[c].length is frameCount
      throw new Error `Channel ${c} has ${pcmArrays[c].length} samples but channel 0 has ${frameCount} — every channel must be the same length`
    c++

  bytesPerSample := bitDepth / 8
  blockAlign := bytesPerSample * numberOfChannels
  dataLength := frameCount * blockAlign

  wav := new Uint8Array HEADER_LENGTH + dataLength
  view := new DataView wav.buffer

  writeAscii := (offset: number, text: string): void ->
    j .= 0
    while j < text.length
      wav[offset + j] = text.charCodeAt j
      j++

  writeAscii 0, "RIFF"
  view.setUint32 4, 36 + dataLength, true   // file length minus the 8-byte RIFF chunk header
  writeAscii 8, "WAVE"
  writeAscii 12, "fmt "
  view.setUint32 16, 16, true               // fmt chunk length
  view.setUint16 20, 1, true                // WAVE_FORMAT_PCM
  view.setUint16 22, numberOfChannels, true
  view.setUint32 24, sampleRate, true
  view.setUint32 28, sampleRate * blockAlign, true
  view.setUint16 32, blockAlign, true
  view.setUint16 34, bitDepth, true
  writeAscii 36, "data"
  view.setUint32 40, dataLength, true

  offset .= HEADER_LENGTH
  frame .= 0
  while frame < frameCount
    channel .= 0
    while channel < numberOfChannels
      sample := pcmArrays[channel][frame]

      if bitDepth is 8
        wav[offset] = quantizeUnsigned8 sample
      else
        // Little endian two's complement. `&` and `>>` coerce to int32, which
        // every depth up to 32 bits fits inside, so the sign extends correctly.
        value .= quantize sample, bitDepth
        b .= 0
        while b < bytesPerSample
          wav[offset + b] = value & 0xFF
          value >>= 8
          b++

      offset += bytesPerSample
      channel++
    frame++

  return wav
